import React, { useId } from 'react';

export type BassitLogoProps = {
  size?: number;
  color?: string;
  spin?: boolean;
  pulse?: boolean;
  converge?: boolean;
  intro?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * BassitAI — animated 8-arm starburst.
 * Pure SVG. No raster fallback. Accepts size and animation flags.
 *
 *   <BassitLogo size={36} intro />            // staggered fade-in on mount
 *   <BassitLogo size={28} pulse />            // gentle breathing
 *   <BassitLogo size={36} converge />         // arms shoot inward (use on submit)
 *   <BassitLogo size={36} spin />             // continuous slow rotation
 *
 * On hover, arms automatically pulse (driven by tokens.css).
 */
export function BassitLogo({
  size = 48,
  color = '#E8713A',
  spin = false,
  pulse = false,
  converge = false,
  intro = false,
  className = '',
  style,
}: BassitLogoProps) {
  const N = 8;
  const arms = Array.from({ length: N }, (_, i) => (i * 360) / N);
  const animClass = [
    spin ? 'logo-spin' : '',
    pulse ? 'logo-pulse' : '',
    converge ? 'logo-converge' : '',
    intro ? 'logo-intro' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const id = useId();
  const gradId = `bg-${id}`;
  const glowId = `gl-${id}`;

  // Proportions: rim r=39, point r=22, half-width 11. Stroke 5.5 for crispness.
  const armPath = 'M -11 -39 L 0 -22 L 11 -39';

  return (
    <svg
      viewBox="-50 -50 100 100"
      width={size}
      height={size}
      className={`bassit-logo ${animClass} ${className}`}
      style={style}
      aria-label="BassitAI"
      role="img"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="-50" x2="0" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor={color} stopOpacity="0.78" />
        </linearGradient>
        <radialGradient id={glowId} cx="0" cy="0" r="42" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="65%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx="0" cy="0" r="42" fill={`url(#${glowId})`} className="logo-glow" />

      <g className="logo-group">
        {arms.map((angle, i) => (
          <g key={i} transform={`rotate(${angle})`} style={{ ['--i' as string]: i, ['--n' as string]: N }}>
            <path
              d={armPath}
              fill="none"
              stroke={`url(#${gradId})`}
              strokeWidth="5.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="logo-arm"
            />
          </g>
        ))}
      </g>
    </svg>
  );
}

export default BassitLogo;
